import React from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { useAuth } from './contexts/AuthContext'
import { useAppSettings } from './hooks/useAppSettings'

// Components
import SmartAppBanner from './components/SmartAppBanner'

// Layouts
import MainLayout from './components/layouts/MainLayout'
import AuthLayout from './components/layouts/AuthLayout'

// Pages
import LoginPage from './pages/LoginPage'
import SignUpPage from './pages/SignUpPage'
import ForgotPasswordPage from './pages/ForgotPasswordPage'
import AuthActionPage from './pages/AuthActionPage'
import SecureAccountPage from './pages/SecureAccountPage'
import DashboardPage from './pages/DashboardPage'
import DiscussionsPage from './pages/DiscussionsPage'
import DiscussionDetailPage from './pages/DiscussionDetailPage'
import AnnouncementsPage from './pages/AnnouncementsPage'
import DirectoryPage from './pages/DirectoryPage'
import ProfilePage from './pages/ProfilePage'
import AdminPage from './pages/AdminPage'
import MessagesPage from './pages/MessagesPage'
import AdminRegistrationPage from './pages/AdminRegistrationPage'
import TermsPage from './pages/TermsPage'
import ResourcesPage from './pages/ResourcesPage'
import PeoplePage from './pages/PeoplePage'
import EventsPage from './pages/EventsPage'

// Protected Route Component
function ProtectedRoute({ children }) {
  const { currentUser, loading, tokenAuthInProgress } = useAuth()
  
  if (loading || tokenAuthInProgress) {
    return (
      <div className="loading-screen">
        <div className="spinner"></div>
        <p>Loading...</p>
      </div>
    )
  }
  
  if (!currentUser) {
    return <Navigate to="/login" replace />
  }
  
  return children
}

// Admin Route Component
function AdminRoute({ children }) {
  const { userProfile, loading, tokenAuthInProgress } = useAuth()
  
  if (loading || tokenAuthInProgress) {
    return (
      <div className="loading-screen">
        <div className="spinner"></div>
        <p>Loading...</p>
      </div>
    )
  }
  
  if (!userProfile?.isAdmin && !userProfile?.isBoardMember && !userProfile?.isSuperAdmin) {
    return <Navigate to="/" replace />
  }
  
  return children
}

// Public Route (redirect if logged in)
function PublicRoute({ children }) {
  const { currentUser, loading, tokenAuthInProgress, socialSignUpInProgress } = useAuth()

  if (loading || tokenAuthInProgress) {
    return (
      <div className="loading-screen">
        <div className="spinner"></div>
        <p>Loading...</p>
      </div>
    )
  }

  // Don't redirect during social sign-up (we need to check if user already exists first)
  if (currentUser && !socialSignUpInProgress) {
    return <Navigate to="/" replace />
  }

  return children
}

// Honors the user's "default landing page" preference. When set to '/'
// (or anything else that would recurse), falls through to the Home
// dashboard so there's no redirect loop.
// Only redirect to the user's chosen landing page ONCE per browser
// session (initial sign-in / tab open). Subsequent clicks on the Home
// sidebar link should still bring them to the real Home dashboard,
// otherwise they'd be stuck ping-ponging between Home and whatever they
// picked as their default (that was the "I can't reach Home" bug).
let didApplyLandingRedirect = false
function LandingRedirect() {
  const { settings } = useAppSettings()
  const target = settings?.defaultLandingPage || '/'
  if (!target || target === '/') return <DashboardPage />
  if (didApplyLandingRedirect) return <DashboardPage />
  didApplyLandingRedirect = true
  return <Navigate to={target} replace />
}

function App() {
  return (
    <>
      {/* Smart App Banner for Android (iOS uses native meta tag) */}
      <SmartAppBanner />

      <Routes>
      {/* Public Routes */}
      <Route element={<AuthLayout />}>
        <Route 
          path="/login" 
          element={
            <PublicRoute>
              <LoginPage />
            </PublicRoute>
          } 
        />
        <Route 
          path="/signup" 
          element={
            <PublicRoute>
              <SignUpPage />
            </PublicRoute>
          } 
        />
        <Route 
          path="/forgot-password" 
          element={
            <PublicRoute>
              <ForgotPasswordPage />
            </PublicRoute>
          } 
        />
      </Route>

      {/* Admin Registration - No Layout */}
      <Route path="/register-admin" element={<AdminRegistrationPage />} />

      {/* Public Terms Page */}
      <Route path="/terms" element={<TermsPage />} />

      {/* Firebase Auth email action handler — handles verifyEmail,
          verifyAndChangeEmail, recoverEmail, and resetPassword links.
          Configured in Firebase Console -> Authentication -> Templates ->
          "Customize action URL" on each template. */}
      <Route path="/auth/action" element={<AuthActionPage />} />

      {/* Crisis-mode "Secure My Account" page reached via the email-change
          heads-up notification. Handles its own auth state — shows the
          login form when signed out, the actions panel when signed in. */}
      <Route path="/secure-account" element={<SecureAccountPage />} />

      {/* Protected Routes */}
      <Route 
        element={
          <ProtectedRoute>
            <MainLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<LandingRedirect />} />
        <Route path="/discussions" element={<DiscussionsPage />} />
        <Route path="/discussions/:id" element={<DiscussionDetailPage />} />
        <Route path="/announcements" element={<AnnouncementsPage />} />
        <Route path="/directory" element={<DirectoryPage />} />
        <Route path="/profile" element={<ProfilePage />} />
        <Route path="/messages" element={<MessagesPage />} />
        <Route path="/directory" element={<DirectoryPage />} />
        <Route path="/resources" element={<ResourcesPage />} />
        <Route path="/people" element={<PeoplePage />} />
        <Route path="/events" element={<EventsPage />} />
        <Route 
          path="/admin/*" 
          element={
            <AdminRoute>
              <AdminPage />
            </AdminRoute>
          } 
        />
      </Route>

        {/* Catch all */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  )
}

export default App
